/**
 * One proposal, and what can be done to it.
 *
 * The page changes shape at `send`, exactly like an invoice does at `issue`:
 *
 * * **A draft** is editable — the lines, the tax rate, the terms — and can be
 *   deleted, because nobody has seen it.
 * * **A sent proposal** is the document the client has. It can be accepted,
 *   declined or withdrawn, and changing what is in it means a revision: a new
 *   document, one version up, carrying the same reference.
 *
 * Accepting offers to open the project. Offers, rather than does: an acceptance
 * is a commercial fact, and whether it becomes a project today is a delivery
 * decision somebody else may want to make.
 */

import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Ban,
  Check,
  Copy,
  FolderKanban,
  Send,
  Trash2,
  X,
} from 'lucide-react';
import {
  Alert,
  api,
  ApiError,
  Button,
  Can,
  Card,
  CardBody,
  CardHeader,
  Checkbox,
  Confirm,
  DetailField,
  DetailGrid,
  ErrorState,
  formatDate,
  formatDateTime,
  formatMoney,
  Input,
  LoadingState,
  Modal,
  PageHeader,
  ScrollArea,
  Select,
  StatusChip,
  Textarea,
  toast,
} from '@connect/web-shared';

import { PROPOSAL_STATUS_TONES } from './proposals-page';
import { ProposalLinesEditor, type DraftLine } from './proposal-lines-editor';

interface Line {
  description: string;
  quantity: string;
  unit_rate: string;
  amount: string;
  taxable: boolean;
}

interface ProposalDetail {
  public_id: string;
  code: string;
  title: string;
  status: string;
  client_public_id: string;
  client_name: string;
  account_manager_name: string | null;
  currency: string;
  tax_rate: string;
  subtotal: string;
  tax_amount: string;
  total: string;
  issued_on: string | null;
  valid_until: string | null;
  expired: boolean;
  version: number;
  summary: string | null;
  terms: string | null;
  decision_note: string | null;
  project_public_id: string | null;
  lines: Line[];
  created_at: string;
}

const CURRENCIES = ['INR', 'USD', 'EUR', 'GBP', 'AED', 'SGD', 'AUD', 'CAD'].map((code) => ({
  value: code,
  label: code,
}));

export function ProposalDetailPage() {
  const { publicId } = useParams<{ publicId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [lines, setLines] = useState<DraftLine[]>([]);
  const [header, setHeader] = useState({
    title: '',
    currency: 'INR',
    tax_rate: '0.00',
    valid_until: '',
    summary: '',
    terms: '',
  });
  const [sending, setSending] = useState(false);
  const [deciding, setDeciding] = useState<'accept' | 'decline' | 'withdraw' | null>(null);
  const [note, setNote] = useState('');
  const [openProject, setOpenProject] = useState(true);
  const [removing, setRemoving] = useState(false);

  const proposal = useQuery({
    queryKey: ['crm', 'proposals', publicId],
    queryFn: () => api.get<ProposalDetail>(`/crm/proposals/${publicId}`),
    enabled: Boolean(publicId),
  });

  useEffect(() => {
    const row = proposal.data;
    if (!row) return;
    setHeader({
      title: row.title,
      currency: row.currency,
      tax_rate: row.tax_rate,
      valid_until: row.valid_until ?? '',
      summary: row.summary ?? '',
      terms: row.terms ?? '',
    });
    setLines(
      row.lines.map((line) => ({
        description: line.description,
        quantity: line.quantity,
        unit_rate: line.unit_rate,
        taxable: line.taxable,
      })),
    );
  }, [proposal.data]);

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['crm', 'proposals'] });
    void queryClient.invalidateQueries({ queryKey: ['projects'] });
  };

  const save = useMutation({
    mutationFn: () =>
      api.patch(`/crm/proposals/${publicId}`, {
        ...header,
        valid_until: header.valid_until || null,
        summary: header.summary.trim() || null,
        terms: header.terms.trim() || null,
        lines: lines
          .filter((line) => line.description.trim() && line.unit_rate.trim())
          .map((line) => ({
            description: line.description.trim(),
            quantity: line.quantity,
            unit_rate: line.unit_rate,
            taxable: line.taxable,
          })),
      }),
    onSuccess: () => {
      invalidate();
      toast.success('Saved');
    },
    onError: (error) => {
      toast.error('Could not save', error instanceof ApiError ? error.message : undefined);
    },
  });

  const act = useMutation({
    mutationFn: ({ path, body }: { path: string; body?: unknown }) =>
      api.post<{ public_id: string }>(`/crm/proposals/${publicId}/${path}`, body),
    onSuccess: (record, variables) => {
      invalidate();
      if (variables.path === 'revise') {
        toast.success('Revision created', 'The original is unchanged.');
        navigate(`/proposals/${record.public_id}`);
        return;
      }
      toast.success('Done');
      setDeciding(null);
      setNote('');
    },
    onError: (error) => {
      toast.error('Could not do that', error instanceof ApiError ? error.message : undefined);
    },
  });

  if (proposal.isLoading) return <LoadingState label="Opening the proposal" />;
  if (proposal.isError) {
    const error = proposal.error;
    return (
      <ErrorState
        title={error instanceof ApiError && error.isNotFound ? 'Proposal not found' : undefined}
        message={error instanceof ApiError ? error.message : undefined}
        requestId={error instanceof ApiError ? error.requestId : undefined}
        onRetry={() => void proposal.refetch()}
      />
    );
  }

  const row = proposal.data;
  if (!row) return null;
  const draft = row.status === 'draft';
  const sent = row.status === 'sent';

  return (
    <div className="space-y-4">
      <PageHeader
        title={row.title}
        description={`${row.code}${row.version > 1 ? ` · version ${row.version}` : ''} · ${row.client_name}`}
        breadcrumbs={[{ label: 'Proposals', to: '/proposals' }, { label: row.code }]}
        actions={
          <>
            <StatusChip value={row.status} tones={PROPOSAL_STATUS_TONES} />
            {draft ? (
              <Can permission="crm.proposal.edit">
                <Button
                  variant="secondary"
                  size="sm"
                  loading={save.isPending}
                  onClick={() => save.mutate()}
                >
                  Save draft
                </Button>
                <Button
                  size="sm"
                  disabled={lines.length === 0}
                  onClick={() => setSending(true)}
                >
                  <Send className="size-3.5" />
                  Mark as sent
                </Button>
              </Can>
            ) : null}
            {sent ? (
              <Can permission="crm.proposal.approve">
                <Button size="sm" onClick={() => setDeciding('accept')}>
                  <Check className="size-3.5" />
                  Accepted
                </Button>
                <Button variant="secondary" size="sm" onClick={() => setDeciding('decline')}>
                  <X className="size-3.5" />
                  Declined
                </Button>
              </Can>
            ) : null}
            {sent ? (
              <Can permission="crm.proposal.edit">
                <Button variant="ghost" size="sm" onClick={() => setDeciding('withdraw')}>
                  <Ban className="size-3.5" />
                  Withdraw
                </Button>
              </Can>
            ) : null}
            {!draft ? (
              <Can permission="crm.proposal.create">
                <Button
                  variant="secondary"
                  size="sm"
                  loading={act.isPending}
                  onClick={() => act.mutate({ path: 'revise' })}
                >
                  <Copy className="size-3.5" />
                  Revise
                </Button>
              </Can>
            ) : null}
            {draft ? (
              <Can permission="crm.proposal.delete">
                <Button variant="ghost" size="sm" onClick={() => setRemoving(true)}>
                  <Trash2 className="size-3.5" />
                </Button>
              </Can>
            ) : null}
          </>
        }
      />

      {draft ? (
        <Alert tone="info" title="This is a draft">
          Nothing has been offered yet. Once it is marked as sent, the figures are the ones the
          client has and changing them means a revision.
        </Alert>
      ) : null}

      {row.expired ? (
        <Alert tone="warning" title="This offer has lapsed">
          It was valid until {formatDate(row.valid_until)}. Revise it with a new date if it is
          still live.
        </Alert>
      ) : null}

      {row.project_public_id ? (
        <Alert tone="success" title="Accepted, and a project was opened">
          <button
            type="button"
            className="mt-1 inline-flex items-center gap-1 text-accent hover:underline"
            onClick={() => navigate(`/projects/${row.project_public_id}`)}
          >
            <FolderKanban className="size-3.5" />
            Open the project
          </button>
        </Alert>
      ) : null}

      {row.decision_note ? (
        <Alert tone={row.status === 'accepted' ? 'success' : 'info'}>
          <strong className="capitalize">{row.status}</strong> — {row.decision_note}
        </Alert>
      ) : null}

      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader
            title={draft ? 'Lines — editable' : 'Lines'}
            description={draft ? undefined : 'Fixed. This is what the client was sent.'}
          />
          <CardBody>
            {draft ? (
              <ProposalLinesEditor
                value={lines}
                onChange={setLines}
                currency={header.currency}
                taxRate={header.tax_rate}
              />
            ) : (
              <ScrollArea className="">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-line text-2xs uppercase tracking-wide text-faint">
                      <th className="px-2 py-2 text-left font-medium">Description</th>
                      <th className="px-2 py-2 text-right font-medium">Qty</th>
                      <th className="px-2 py-2 text-right font-medium">Rate</th>
                      <th className="px-2 py-2 text-right font-medium">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {row.lines.map((line, index) => (
                      <tr key={index} className="border-b border-line/60">
                        <td className="px-2 py-2.5 text-body">
                          {line.description}
                          {!line.taxable ? (
                            <span className="ml-1.5 text-2xs text-faint">(no tax)</span>
                          ) : null}
                        </td>
                        <td className="px-2 py-2.5 text-right text-muted tabular">
                          {line.quantity}
                        </td>
                        <td className="px-2 py-2.5 text-right text-muted tabular">
                          {formatMoney(line.unit_rate, row.currency)}
                        </td>
                        <td className="px-2 py-2.5 text-right font-medium text-body tabular">
                          {formatMoney(line.amount, row.currency)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={3} className="px-2 py-1.5 text-right text-muted">
                        Subtotal
                      </td>
                      <td className="px-2 py-1.5 text-right tabular">
                        {formatMoney(row.subtotal, row.currency)}
                      </td>
                    </tr>
                    <tr>
                      <td colSpan={3} className="px-2 py-1.5 text-right text-muted">
                        Tax at {row.tax_rate}%
                      </td>
                      <td className="px-2 py-1.5 text-right tabular">
                        {formatMoney(row.tax_amount, row.currency)}
                      </td>
                    </tr>
                    <tr className="border-t border-line">
                      <td colSpan={3} className="px-2 py-2 text-right font-semibold text-heading">
                        Total
                      </td>
                      <td className="px-2 py-2 text-right font-semibold text-heading tabular">
                        {formatMoney(row.total, row.currency)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </ScrollArea>
            )}
          </CardBody>
        </Card>

        <div className="space-y-4">
          <Card>
            <CardHeader title="Details" />
            <CardBody className="space-y-4">
              {draft ? (
                <>
                  <Input
                    label="Title"
                    value={header.title}
                    onChange={(event) =>
                      setHeader((h) => ({ ...h, title: event.target.value }))
                    }
                  />
                  <div className="grid gap-3 sm:grid-cols-2">
                    <Select
                      label="Currency"
                      options={CURRENCIES}
                      value={header.currency}
                      onChange={(event) =>
                        setHeader((h) => ({ ...h, currency: event.target.value }))
                      }
                    />
                    <Input
                      label="Tax rate (%)"
                      inputMode="decimal"
                      value={header.tax_rate}
                      onChange={(event) =>
                        setHeader((h) => ({ ...h, tax_rate: event.target.value }))
                      }
                    />
                  </div>
                  <Input
                    label="Valid until"
                    type="date"
                    value={header.valid_until}
                    onChange={(event) =>
                      setHeader((h) => ({ ...h, valid_until: event.target.value }))
                    }
                  />
                </>
              ) : (
                <DetailGrid className="sm:grid-cols-1">
                  <DetailField label="Client">
                    <button
                      type="button"
                      className="text-accent hover:underline"
                      onClick={() => navigate(`/clients/${row.client_public_id}`)}
                    >
                      {row.client_name}
                    </button>
                  </DetailField>
                  <DetailField label="Owner">{row.account_manager_name ?? '—'}</DetailField>
                  <DetailField label="Sent">{formatDate(row.issued_on)}</DetailField>
                  <DetailField label="Valid until">{formatDate(row.valid_until)}</DetailField>
                  <DetailField label="Drafted">{formatDateTime(row.created_at)}</DetailField>
                </DetailGrid>
              )}
            </CardBody>
          </Card>

          <Card>
            <CardHeader title="Summary and terms" />
            <CardBody className="space-y-3">
              {draft ? (
                <>
                  <Textarea
                    label="Summary"
                    rows={3}
                    value={header.summary}
                    onChange={(event) =>
                      setHeader((h) => ({ ...h, summary: event.target.value }))
                    }
                  />
                  <Textarea
                    label="Terms"
                    rows={3}
                    value={header.terms}
                    onChange={(event) => setHeader((h) => ({ ...h, terms: event.target.value }))}
                  />
                </>
              ) : (
                <>
                  <p className="whitespace-pre-line text-sm text-body">{row.summary || '—'}</p>
                  {row.terms ? (
                    <p className="whitespace-pre-line border-t border-line pt-3 text-xs text-muted">
                      {row.terms}
                    </p>
                  ) : null}
                </>
              )}
            </CardBody>
          </Card>
        </div>
      </div>

      <Confirm
        open={sending}
        title={`Mark ${row.code} as sent?`}
        message={`${formatMoney(row.total, row.currency)} to ${row.client_name}. After this the lines and the total are fixed — changing them means a revision, which is a new document.`}
        confirmLabel="Mark as sent"
        variant="primary"
        onClose={() => setSending(false)}
        onConfirm={async () => {
          // Saved first: what is on screen is what should be sent.
          await save.mutateAsync();
          await act.mutateAsync({ path: 'send' });
          setSending(false);
        }}
      />

      <Confirm
        open={removing}
        title={`Delete ${row.code}?`}
        message="It is still a draft, so nobody has seen it. Only drafts can be deleted — a sent proposal is withdrawn instead."
        confirmLabel="Delete"
        onClose={() => setRemoving(false)}
        onConfirm={async () => {
          await api.delete(`/crm/proposals/${publicId}`);
          invalidate();
          toast.success('Draft deleted');
          navigate('/proposals');
        }}
      />

      <Modal
        open={deciding !== null}
        onClose={() => setDeciding(null)}
        title={
          deciding === 'accept'
            ? 'They accepted'
            : deciding === 'decline'
              ? 'They declined'
              : 'Withdraw this proposal'
        }
        size="sm"
        footer={
          <>
            <Button variant="secondary" size="sm" onClick={() => setDeciding(null)}>
              Cancel
            </Button>
            <Button
              size="sm"
              variant={deciding === 'accept' ? 'primary' : 'danger'}
              loading={act.isPending}
              disabled={deciding !== 'accept' && !note.trim()}
              onClick={() =>
                act.mutate({
                  path:
                    deciding === 'accept'
                      ? 'accept'
                      : deciding === 'decline'
                        ? 'decline'
                        : 'withdraw',
                  body: {
                    note: note.trim() || null,
                    create_project: deciding === 'accept' ? openProject : false,
                  },
                })
              }
            >
              {deciding === 'accept' ? 'Record acceptance' : 'Record it'}
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <Textarea
            label="Note"
            required={deciding !== 'accept'}
            rows={3}
            value={note}
            onChange={(event) => setNote(event.target.value)}
            hint={
              deciding === 'accept'
                ? 'Optional. Anything worth remembering about how it was agreed.'
                : 'Required. Without it there is nothing to learn from this one.'
            }
          />
          {deciding === 'accept' ? (
            <Checkbox
              label="Open a project for this work"
              checked={openProject}
              onChange={(event) => setOpenProject(event.target.checked)}
              hint="A fixed-price project, budgeted at the agreed figure net of tax. It starts as a draft."
            />
          ) : null}
        </div>
      </Modal>
    </div>
  );
}
